import { Line, mixins } from 'vue-chartjs';

export default {
    extends: Line,
    mixins: [mixins.reactiveProp],
    props: ['chartData', 'options'],
    data() {
        return {
            defaultOptions: {
                //Chart.js options
                layout: {
                    padding: {
                        left: 1
                    }
                },
                scales: {
                    yAxes: [
                        {
                            position: 'left',
                            ticks: {
                                beginAtZero: true,
                                fontColor: 'rgb(25 118 210)',
                                borderColor: 'rgb(25 118 210)',
                                padding: 10,
                                mirror: false,
                                autoSkip: false,
                                maxTicksLimit: 8
                            },
                            gridLines: {
                                display: false,
                                fontColor: 'rgb(25 118 210)',
                                borderColor: 'rgb(25 118 210)',
                                color: 'rgb(25 118 210)'
                            }
                        }
                    ],
                    xAxes: [
                        {
                            gridLines: {
                                display: false,
                                color: 'rgb(25 118 210)',
                                borderColor: 'rgb(25 118 210)'
                            },
                            ticks: {
                                fontColor: 'rgb(25 118 210)',
                                borderColor: 'rgb(25 118 210)',
                                padding: 10
                            },
                            categoryPercentage: 0.85,
                            barPercentage: 0.9,
                            offset: true
                        }
                    ]
                },
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        usePointStyle: true,
                        pointStyle: true,
                        fontColor: 'rgb(25 118 210)'
                    },
                    onClick: function(e, legendItem) {
                        const index = legendItem.datasetIndex;
                        const chart = this.chart;
                        const meta = chart.getDatasetMeta(index);

                        meta.hidden =
                            meta.hidden === null
                                ? !chart.data.datasets[index].hidden
                                : null;
                        chart.update();
                    }
                },
                responsive: true,
                maintainAspectRatio: false,
                borderColor: 'rgb(25 118 210)',
                plugins: {
                    datalabels: {
                        color: 'rgb(25 118 210)',
                        font: {
                            weight: 'bold'
                        },
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        borderRadius: 4,
                        padding: 2
                    }
                }
            },
            isUpdating: false
        };
    },
    mounted() {
        const chartData = this.chartData || this.chartdata;

        if (!chartData) {
            console.warn('Chart data is undefined or null');
            return;
        }

        const mergeWithFunctions = (target, source) => {
            if (!source) return target;

            const result = { ...target };

            Object.keys(source).forEach(key => {
                if (key === 'scales' && source[key]) {
                    result[key] = this.deepMergeScales(
                        result[key] || {},
                        source[key]
                    );
                } else if (
                    key === 'legend' &&
                    source[key] &&
                    source[key].onClick
                ) {
                    result[key] = { ...result[key], ...source[key] };
                } else if (
                    typeof source[key] === 'object' &&
                    source[key] !== null &&
                    !Array.isArray(source[key])
                ) {
                    result[key] = mergeWithFunctions(
                        result[key] || {},
                        source[key]
                    );
                } else {
                    result[key] = source[key];
                }
            });

            return result;
        };

        const chartOptions = chartData.options || {};
        const propOptions = this.options || {};

        if (!chartOptions.legend) chartOptions.legend = {};
        if (!chartOptions.legend.onClick) {
            chartOptions.legend.onClick = (e, legendItem) => {
                const ci = this.$data._chart;
                const meta = ci.getDatasetMeta(legendItem.datasetIndex);

                meta.hidden =
                    meta.hidden === null
                        ? !ci.data.datasets[legendItem.datasetIndex].hidden
                        : null;
                ci.update();
            };
        }

        const mergedOptions = mergeWithFunctions(
            this.defaultOptions,
            mergeWithFunctions(chartOptions, propOptions)
        );

        this.renderChart(chartData, mergedOptions);
    },
    methods: {
        deepMergeScales(targetScales, sourceScales) {
            const result = { ...targetScales };

            if (sourceScales.xAxes && sourceScales.xAxes.length > 0) {
                if (!result.xAxes) result.xAxes = [];

                for (let i = 0; i < sourceScales.xAxes.length; i++) {
                    const sourceAxis = sourceScales.xAxes[i];
                    if (i >= result.xAxes.length) {
                        result.xAxes.push({ ...sourceAxis });
                    } else {
                        if (sourceAxis.stacked !== undefined) {
                            result.xAxes[i] = {
                                ...result.xAxes[i],
                                ...sourceAxis,
                                stacked: sourceAxis.stacked
                            };
                        } else {
                            result.xAxes[i] = {
                                ...result.xAxes[i],
                                ...sourceAxis
                            };
                        }
                    }
                }
            }

            if (sourceScales.yAxes && sourceScales.yAxes.length > 0) {
                if (!result.yAxes) result.yAxes = [];

                for (let i = 0; i < sourceScales.yAxes.length; i++) {
                    const sourceAxis = sourceScales.yAxes[i];
                    if (i >= result.yAxes.length) {
                        result.yAxes.push({ ...sourceAxis });
                    } else {
                        if (sourceAxis.stacked !== undefined) {
                            result.yAxes[i] = {
                                ...result.yAxes[i],
                                ...sourceAxis,
                                stacked: sourceAxis.stacked,
                                ticks: sourceAxis.ticks
                                    ? {
                                          ...result.yAxes[i].ticks,
                                          ...sourceAxis.ticks
                                      }
                                    : result.yAxes[i].ticks
                            };
                        } else {
                            result.yAxes[i] = {
                                ...result.yAxes[i],
                                ...sourceAxis
                            };
                        }
                    }
                }
            }

            return result;
        },

        updateChart() {
            if (this.$data._chart) {
                this.$data._chart.update();
            }
        }
    },
    watch: {
        chartData: {
            handler(newData, oldData) {
                if (this.isUpdating) return;

                if (newData === oldData || !newData) return;

                this.isUpdating = true;

                this.$nextTick(() => {
                    try {
                        if (this.$data._chart) {
                            this.$data._chart.destroy();
                        }

                        const mergeWithFunctions = (target, source) => {
                            if (!source) return target;

                            const result = { ...target };

                            Object.keys(source).forEach(key => {
                                if (key === 'scales' && source[key]) {
                                    result[key] = this.deepMergeScales(
                                        result[key] || {},
                                        source[key]
                                    );
                                } else if (
                                    key === 'legend' &&
                                    source[key] &&
                                    source[key].onClick
                                ) {
                                    result[key] = {
                                        ...result[key],
                                        ...source[key]
                                    };
                                } else if (
                                    typeof source[key] === 'object' &&
                                    source[key] !== null &&
                                    !Array.isArray(source[key])
                                ) {
                                    result[key] = mergeWithFunctions(
                                        result[key] || {},
                                        source[key]
                                    );
                                } else {
                                    result[key] = source[key];
                                }
                            });

                            return result;
                        };

                        const chartOptions = newData.options || {};
                        const propOptions = this.options || {};

                        if (!chartOptions.legend) chartOptions.legend = {};
                        if (!chartOptions.legend.onClick) {
                            chartOptions.legend.onClick = (e, legendItem) => {
                                const ci = this.$data._chart;
                                const meta = ci.getDatasetMeta(
                                    legendItem.datasetIndex
                                );

                                meta.hidden =
                                    meta.hidden === null
                                        ? !ci.data.datasets[
                                              legendItem.datasetIndex
                                          ].hidden
                                        : null;
                                ci.update();
                            };
                        }

                        const mergedOptions = mergeWithFunctions(
                            this.defaultOptions,
                            mergeWithFunctions(chartOptions, propOptions)
                        );

                        this.renderChart(newData, mergedOptions);
                    } finally {
                        this.isUpdating = false;
                    }
                });
            },
            deep: true
        }
    }
};
